// @flow

import React from 'react';
import type { KPIs, ShoppingCenterGemini, VisioglobeUnitsOk } from '../../../../types/geminiType';
import {
  erv,
  glaWithoutSst,
  glaWithSst,
  invSbr,
  occupancyCost,
  ocr,
  revPot,
  salesR12m,
  spotMgr,
} from './geminiConst';
import { all, OCR_QUANTIL_LEGEND } from '../../../../const';
import { computeOcupancyCost, getCurrentQuartil, glaDivider } from './geminiFunctions';
import CurrencySurfaceConvertor from '../../../common/CurrencySurfaceConvertor';
import SurfaceConvertor from '../../../common/SurfaceConvertor';
import { isKPIValuesReversed } from '../../../../utils/utils';

type Props = {
  visioglobeUnits: VisioglobeUnitsOk[],
  currentKpiFilter: string,
  storeCategory: string,
  shoppingCenterData: ShoppingCenterGemini,
  isCurrencyBySurface: boolean,
  className?: string,
}

class QuantilsLegend extends React.Component<Props> {
  static defaultProps = {
    className: '',
  }
  getExtremum = (
    visioglobeUnits: VisioglobeUnitsOk[],
    kpi: string,
    storeCategory: string,
    min: boolean,
  ) => {
    const res = visioglobeUnits.filter(store =>
      store.kpi
      && (store.kpi[kpi] || kpi === occupancyCost)
      && (storeCategory === all
        || (store.glaSplit && store.glaSplit.toLowerCase() === storeCategory)
      ))
      .sort((a, b) => {
        const bef = min ? a : b;
        const aft = min ? b : a;
        return kpi === occupancyCost ?
          (computeOcupancyCost(bef.kpi) - computeOcupancyCost(aft.kpi)) :
          (bef.kpi[kpi] - aft.kpi[kpi]);
      })[0];
    if (kpi === ocr) {
      return min ? OCR_QUANTIL_LEGEND.q0 : OCR_QUANTIL_LEGEND.q6;
    }
    if (res && res.kpi) {
      return this.getConvertedKpi(res.kpi, kpi);
    }
    return 'N/A';
  };

  getQuartil = (quartilNumber: string, kpi: string) => {
    if (kpi === ocr) {
      return OCR_QUANTIL_LEGEND[quartilNumber];
    }
    const quartil: KPIs = getCurrentQuartil(
      this.props.shoppingCenterData,
      this.props.storeCategory,
    )[quartilNumber]
      .sort((a, b) => b.year - a.year)[0];
    if (!quartil) {
      return 'NA';
    }
    return this.getConvertedKpi(quartil, kpi);
  };

  getConvertedKpi = (kpi: KPIs, kpiSelected: string) => {
    switch (kpiSelected) {
      case ocr:
        return <span>{kpi.ocr ? kpi.ocr.toFixed(1) : 'N/A'} %</span>;
      // case dwellPercent:
      //   return <span>{kpi.dwellPercent ? (kpi.dwellPercent / 100).toFixed(1) : 'N/A'} %</span>;
      case salesR12m:
        return (<CurrencySurfaceConvertor
          gla={
            glaDivider(
              salesR12m,
              this.props.shoppingCenterData,
              this.props.storeCategory,
            )
          }
          value={this.props.isCurrencyBySurface ? kpi.salesNum : kpi.salesR12m}
          withSymbol
        />);
      case glaWithSst:
        return (<SurfaceConvertor value={kpi.glaWithSst} />);
      case glaWithoutSst:
        return (<SurfaceConvertor value={kpi.glaWithoutSst} />);
      case erv:
        return (<CurrencySurfaceConvertor
          gla={
            glaDivider(
              erv,
              this.props.shoppingCenterData,
              this.props.storeCategory,
            )
          }
          value={kpi.erv}
          withSymbol
        />);
      case spotMgr:
        return (<CurrencySurfaceConvertor
          gla={
            glaDivider(
              spotMgr,
              this.props.shoppingCenterData,
              this.props.storeCategory,
            )
          }
          value={kpi.spotMgr}
          withSymbol
        />);
      case invSbr:
        return (<CurrencySurfaceConvertor
          gla={
            glaDivider(
              invSbr,
              this.props.shoppingCenterData,
              this.props.storeCategory,
            )
          }
          value={kpi.invSbr}
          withSymbol
        />);
      case occupancyCost:
        return (<CurrencySurfaceConvertor
          gla={
            glaDivider(
              occupancyCost,
              this.props.shoppingCenterData,
              this.props.storeCategory,
            )
          }
          value={computeOcupancyCost(kpi)}
          withSymbol
        />);
      case revPot:
        return (<CurrencySurfaceConvertor
          gla={
            glaDivider(
              revPot,
              this.props.shoppingCenterData,
              this.props.storeCategory,
            )
          }
          value={kpi.revPot}
          withSymbol
        />);
      default:
        return 'N/A';
    }
  };

  render() {
    return (
      <div className={this.props.className}>
        <div className="heatmap-indicator-quartiles">
          <div className="quartil-tooltip">
            <div className="quartil-text">
              {
                this.getExtremum(
                  this.props.visioglobeUnits,
                  this.props.currentKpiFilter,
                  this.props.storeCategory,
                  true,
                )
              }
            </div>
            <div className="down-arrow" />
          </div>
          <div className="quartil-tooltip">
            <div className="quartil-text">
              {this.getQuartil('q1', this.props.currentKpiFilter)}
            </div>
            <div className="down-arrow" />
          </div>
          <div className="quartil-tooltip">
            <div className="quartil-text">
              {this.getQuartil('q2', this.props.currentKpiFilter)}
            </div>
            <div className="down-arrow" />
          </div>
          <div className="quartil-tooltip">
            <div className="quartil-text">
              {this.getQuartil('q3', this.props.currentKpiFilter)}
            </div>
            <div className="down-arrow" />
          </div>
          <div className="quartil-tooltip">
            <div className="quartil-text">
              {this.getQuartil('q4', this.props.currentKpiFilter)}
            </div>
            <div className="down-arrow" />
          </div>
          <div className="quartil-tooltip">
            <div className="quartil-text">
              {this.getQuartil('q5', this.props.currentKpiFilter)}
            </div>
            <div className="down-arrow" />
          </div>
          <div className="quartil-tooltip">
            <div className="quartil-text">
              {
                this.getExtremum(
                  this.props.visioglobeUnits,
                  this.props.currentKpiFilter,
                  this.props.storeCategory,
                  false,
                )
              }
            </div>
            <div className="down-arrow" />
          </div>
        </div>
        <div className="heatmap-indicator-container">
          <div />
          <div className={`heatmap-indicator${this.props.currentKpiFilter === ocr ? ' ocr-heatmap' : ''}`}>
            <div
              className={isKPIValuesReversed(this.props.currentKpiFilter) ? 'veryVeryLow' : 'veryVeryHigh'}
            />
            <div className={isKPIValuesReversed(this.props.currentKpiFilter) ? 'veryLow' : 'veryHigh'} />
            <div className={isKPIValuesReversed(this.props.currentKpiFilter) ? 'low' : 'high'} />
            <div className={isKPIValuesReversed(this.props.currentKpiFilter) ? 'high' : 'low'} />
            <div className={isKPIValuesReversed(this.props.currentKpiFilter) ? 'veryHigh' : 'veryLow'} />
            <div
              className={isKPIValuesReversed(this.props.currentKpiFilter) ? 'veryVeryHigh' : 'veryVeryLow'}
            />
          </div>
          <div />
        </div>
      </div>
    );
  }
}

export default QuantilsLegend;
